import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from './ui/input';

const CITIES = [
  // Vaucluse (84)
  'Sainte-Cécile-les-Vignes', 'Orange', 'Avignon', 'Carpentras', 'Cavaillon', 'Apt',
  'Sorgues', 'Le Pontet', 'Bollène', 'Monteux', 'Entraigues-sur-la-Sorgue',
  'Vedène', 'Pernes-les-Fontaines', 'Valréas', 'Morières-lès-Avignon',
  'L\'Isle-sur-la-Sorgue', 'Le Thor', 'Châteauneuf-du-Pape', 'Bédarrides',
  'Courthézon', 'Jonquières', 'Sarrians', 'Camaret-sur-Aigues',
  'Vaison-la-Romaine', 'Mazan', 'Althen-des-Paluds', 'Aubignan',
  'Beaumes-de-Venise', 'Malaucène', 'Gordes', 'Bonnieux', 'Roussillon',
  // Gard (30)
  'Nîmes', 'Bagnols-sur-Cèze', 'Beaucaire', 'Uzès',
  'Villeneuve-lès-Avignon', 'Les Angles', 'Pont-Saint-Esprit',
  'Roquemaure', 'Remoulins', 'Laudun-l\'Ardoise',
  // Drôme (26)
  'Montélimar', 'Pierrelatte', 'Nyons', 'Saint-Paul-Trois-Châteaux',
  'Donzère', 'Buis-les-Baronnies', 'Grignan',
  // Bouches-du-Rhône (13)
  'Arles', 'Tarascon', 'Saint-Rémy-de-Provence', 'Les Baux-de-Provence',
  'Châteaurenard', 'Graveson', 'Noves', 'Rognonas',
  // Ardèche (07)
  'Bourg-Saint-Andéol', 'Viviers',
].sort((a, b) => a.localeCompare(b, 'fr'));

const VilleAutocomplete = ({ value, onChange, name = 'ville', id, required, placeholder, className }) => {
  const [open, setOpen] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  const filter = useCallback((val) => {
    if (!val || val.length < 1) return [];
    const q = val.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return CITIES.filter(c =>
      c.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(q)
    ).slice(0, 8);
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    onChange(e);
    const matches = filter(val);
    setFiltered(matches);
    setOpen(matches.length > 0);
    setHighlightIdx(-1);
  };

  const selectCity = (city) => {
    const syntheticEvent = { target: { name, value: city } };
    onChange(syntheticEvent);
    setOpen(false);
    setHighlightIdx(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0) {
      e.preventDefault();
      selectCity(filtered[highlightIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (listRef.current && highlightIdx >= 0) {
      const item = listRef.current.children[highlightIdx];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightIdx]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <Input
          id={id}
          name={name}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            const matches = filter(value);
            if (matches.length > 0) { setFiltered(matches); setOpen(true); }
          }}
          required={required}
          placeholder={placeholder || 'Orange, Avignon, Carpentras...'}
          className={`pl-9 ${className || ''}`}
          autoComplete="off"
          data-testid="ville-autocomplete-input"
        />
      </div>
      {open && filtered.length > 0 && (
        <ul
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          data-testid="ville-suggestions-list"
        >
          {filtered.map((city, idx) => (
            <li
              key={city}
              onMouseDown={() => selectCity(city)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 transition-colors ${
                idx === highlightIdx
                  ? 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'
              }`}
              data-testid={`ville-option-${city}`}
            >
              <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
              {city}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VilleAutocomplete;
